import { Box, Button, Flex, Heading, Input, InputGroup, SimpleGrid, Text } from '@chakra-ui/react';
import { UseFormReturn } from 'react-hook-form';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { SelectField } from 'src/components/SelectField';
import { WonTitleFilters } from '../../types';
import { Club, Competition } from '@/model';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TeamFilterSelect from '@/components/TeamFilterSelect';

export interface WonTitleFilterBoxProps {
  filtersForm: UseFormReturn<WonTitleFilters>;
  competitions: Competition[] | undefined;
  clubs: Club[];
  forceRefetch: () => void;
}

export const WonTitleFilterBox = ({ filtersForm, clubs, competitions }: WonTitleFilterBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, setValue } = filtersForm;
  const debounceRef = useRef<any>(null);

  const [year, setYear] = useState('');

  const handleReset = () => {
    setYear('');
    reset();
  };

  const handleYearChange = useCallback((e: any) => {
    const raw = e.target.value;
    const cleaned = raw.replace(/\D/g, '').slice(0, 4);
    setYear(cleaned);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setValue('year', year);
    }, 500);

    return () => clearTimeout(debounceRef.current);
  }, [year, setValue]);

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>
          {t('pages.application_won_title.components.won_title_filter_box.won_title_filter_title')}
        </Heading>
      </Flex>

      <Flex direction={{ base: 'row' }} gap='small' rowGap='micro'>
        <SimpleGrid
          gap='deka'
          alignItems='flex-end'
          columns={{ base: 1, sm: 2, md: 4, lg: 5, xl: 6 }}
          rowGap='xxsmall'
          flexGrow={1}
        >
          <TeamFilterSelect showFlag name='team_uuid' control={filtersForm.control} clubs={clubs} width='100%' />

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_won_title.components.won_title_filter_box.won_title_filter_competition_label')}
            </Text>
            <SelectField {...register('competition_uuid')}>
              <option value=''>
                {t('pages.application_won_title.components.won_title_filter_box.won_title_filter_competition_default_option')}
              </option>
              {competitions?.map(item => (
                <option key={item.uuid} value={item.uuid}>
                  {item.name}
                </option>
              ))}
            </SelectField>
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_won_title.components.won_title_filter_box.won_title_filter_year_label')}
            </Text>
            <Input variant='flushed' value={year} onChange={handleYearChange} />
          </InputGroup>
        </SimpleGrid>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={handleReset}>
          {t('pages.application_won_title.components.won_title_filter_box.won_title_filter_clear_button')}
        </Button>
      </Flex>
    </Box>
  );
};
